import Page from '../../components/Page';
import PageLayout from '../../components/PageLayout/PageLayout';
import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import { ArrowLeft } from '@raydiant/raydial/components/Icons';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import * as paths from '../../routes/paths';
import Card from '@raydiant/raydial/components/Card';
import NumberOfPlans from './NumberOfPlans';
import Divider from '@raydiant/raydial/components/Divider';
import OrderDetails from './OrderDetails';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { show as intercomShow } from '@intercom/messenger-js-sdk';
import PlanDetailsCard from './PlanDetailsCard';
import useCreateStripeCheckout from '../../hooks/useCreateStripeCheckout';
import useGetStripeCheckoutStatus from '../../hooks/useGetStripeCheckoutStatus';
import useGetStripeProducts from '../../hooks/useGetStripeProducts';
import config from '../../config';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import CheckoutPageLayout from './component/CheckoutPageLayout';
import CheckoutSuccessPage from './component/CheckoutSuccessPage';
import { useToastState } from '@raydiant/raydial/components/Toast';
import CheckoutErrorPage from './component/CheckoutErrorPage';
import CheckoutMaxRetryErrorPage from './component/CheckoutMaxRetryErrorPage';
import {
  MIN_PLAN_COUNT,
  DEFAULT_PLAN_COUNT,
  MAX_POLL_COUNT,
} from './constants';
import { BillingFrequency } from '@raydiant/api-client-js';
import Spinner from '@raydiant/raydial/components/Spinner';
import analytics from '../../clients/analyticsClient';
import {
  getPlanDetail,
  getMaxPlanCount,
  isBlackFridayActive,
} from '../../utilities';
import { transformStripeProductsToPlan } from './utils/transformStripeProducts';

const { stripePublicKey } = config;

const stripePromise = loadStripe(stripePublicKey);

const PlansPage = () => {
  const history = useHistory();
  const location = useLocation();
  const state = useToastState();
  const { productIdentifier } = useParams<{ productIdentifier: string }>();

  // State
  // Plan Select Page
  const [billingCycle] = useState<BillingFrequency>('monthly');
  const [count, setCount] = useState(DEFAULT_PLAN_COUNT);

  // Checkout Flow
  const [clientSecret, setClientSecret] = useState<string>('');

  // Refs
  const pollCountRef = useRef(0);

  // Memoized URL params
  const urlSessionId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('session_id');
  }, [location.search]);

  // Hooks
  const {
    data: stripeProducts,
    isLoading: isLoadingProducts,
    isError: isProductsError,
  } = useGetStripeProducts(productIdentifier || 'pro');

  const { mutate: createCheckout, isLoading: isCreateCheckoutLoading } =
    useCreateStripeCheckout();
  const {
    data: checkoutStatus,
    isError: isCheckoutError,
    isLoading: isCheckoutLoading,
    refetch: refetchCheckoutStatus,
  } = useGetStripeCheckoutStatus(urlSessionId || '', {
    enabled: false,
  });

  // API 데이터를 Plan 구조로 변환
  const planDetail = useMemo(() => {
    if (stripeProducts && stripeProducts.length > 0) {
      try {
        return transformStripeProductsToPlan(
          stripeProducts,
          productIdentifier || 'pro',
        );
      } catch (error) {
        // 변환 실패 시 콘솔에 에러 로그 출력하고 fallback 사용
        console.error('Failed to transform Stripe products:', error);
        return getPlanDetail();
      }
    }
    // Fallback: API 데이터가 없으면 하드코딩된 데이터 사용
    return getPlanDetail();
  }, [stripeProducts, productIdentifier]);

  const maxPlanCount = getMaxPlanCount();

  // Auto-adjust billing cycle if selected one is not available
  const effectiveBillingCycle = useMemo(() => {
    if (planDetail[billingCycle]) {
      return billingCycle;
    }
    // Fallback to available billing cycle
    if (planDetail.annually) {
      return 'annually';
    }
    if (planDetail.monthly) {
      return 'monthly';
    }
    return billingCycle; // Keep original if neither is available
  }, [billingCycle, planDetail]);

  const selectedPlan = useMemo(
    () => planDetail[effectiveBillingCycle],
    [effectiveBillingCycle, planDetail],
  );

  // Effects
  useEffect(() => {
    if (!urlSessionId) {
      analytics.trackPageView({ page: 'pre_checkout' });
      return;
    }

    pollCountRef.current = 0;

    refetchCheckoutStatus();

    const intervalId = setInterval(() => {
      if (pollCountRef.current >= MAX_POLL_COUNT) {
        clearInterval(intervalId);
        return;
      }

      pollCountRef.current += 1;
      refetchCheckoutStatus();
    }, 3000);

    return () => clearInterval(intervalId);
  }, [urlSessionId, refetchCheckoutStatus]);

  const openChat = useCallback(() => intercomShow(), []);

  // const handleBillingCycleChange = useCallback(
  //   (newBillingCycle: BillingFrequency) => {
  //     // segment tracking
  //     analytics.trackBillingCycleChanged({
  //       plan: 'pro',
  //       billingCycle: newBillingCycle,
  //     });
  //     setBillingCycle(newBillingCycle);
  //   },
  //   [],
  // );

  // Handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value === '') {
        setCount(0);
        return;
      }

      const numValue = parseInt(value, 10);

      if (!isNaN(numValue) && numValue >= 0) {
        //  segment tracking
        analytics.trackQuantityChanged({
          plan: 'pro',
          quantity: numValue,
          billingCycle,
        });
        setCount(numValue);
      }
    },
    [billingCycle],
  );

  const handleDecrement = useCallback(() => {
    setCount((prevCount) => {
      if (prevCount > MIN_PLAN_COUNT) {
        //  segment tracking
        analytics.trackQuantityChanged({
          plan: 'pro',
          quantity: prevCount - 1,
          billingCycle,
        });
      }
      return prevCount > MIN_PLAN_COUNT ? prevCount - 1 : prevCount;
    });
  }, [billingCycle]);

  const handleIncrement = useCallback(() => {
    setCount((prevCount) => {
      //  segment tracking
      analytics.trackQuantityChanged({
        plan: 'pro',
        quantity: prevCount + 1,
        billingCycle,
      });
      return prevCount + 1;
    });
  }, [billingCycle]);

  const handleInputBlur = useCallback(() => {
    setCount((prevCount) =>
      prevCount < MIN_PLAN_COUNT ? MIN_PLAN_COUNT : prevCount,
    );
  }, []);

  const handleReviewOrder = useCallback(() => {
    createCheckout(
      {
        quantity: count,
        billingFrequency: billingCycle,
        requiredShippingCharges: true,
        productIdentifier: planDetail.productIdentifier,
        returnUrl: `/plans/${planDetail.productIdentifier}`,
      },
      {
        onSuccess: (data) => {
          // segment tracking
          analytics.trackReviewOrderClicked({
            plan: 'pro',
            quantity: count,
            billingCycle,
          });
          setClientSecret(data.clientSecret);
        },
        onError: () => {
          state.add({
            title: 'Something went wrong',
            description: 'Please try again',
            variant: 'caution',
          });
        },
      },
    );
  }, [
    count,
    billingCycle,
    createCheckout,
    state,
    planDetail.productIdentifier,
  ]);

  const handleBackToGettingStarted = useCallback(() => {
    history.push(paths.gettingStarted());
  }, [history]);

  const handleBackFromCheckout = useCallback(() => {
    // segment tracking
    analytics.trackStripeCheckoutAbandoned({});
    setClientSecret('');
    history.replace(paths.plans(productIdentifier || 'pro'));
  }, [history, productIdentifier]);

  const handleRetryPayment = useCallback(() => {
    history.replace(paths.plans(productIdentifier || 'pro'));
  }, [history, productIdentifier]);

  const openStripeCustomerPortal = useCallback(() => {
    analytics.trackInvoiceCTAClicked({});
    history.push(paths.account());
  }, [history]);

  // Computed values
  const showStripeEmbeddedCheckout = !!clientSecret;
  const isCheckoutCompleted = checkoutStatus?.status === 'complete';
  const isCheckoutProcessing =
    !isCheckoutCompleted && !isCheckoutError && isCheckoutLoading;
  const isInitialCheckoutPolling =
    isCheckoutProcessing && pollCountRef.current === 0;
  const isCheckoutTimeout =
    pollCountRef.current >= MAX_POLL_COUNT &&
    !isCheckoutCompleted &&
    !isCheckoutError;

  const pageTitle = useMemo(
    () => (
      <div>
        <div className="flex text-black gap-4">
          <Button variant="unstyled" onPress={handleBackToGettingStarted}>
            <ArrowLeft size={24} />
          </Button>
          <Text variant="header" as="h1" className="text-black">
            Get a {planDetail.name} Plan
          </Text>
        </div>

        <p className="text-xl gray-400">
          Select how many screens you need a plan for
        </p>
      </div>
    ),
    [handleBackToGettingStarted, planDetail.name],
  );

  const renderContent = useMemo(() => {
    // Loading products from API
    if (isLoadingProducts) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-8">
          <Spinner size="large" />
          <Text variant="smallSubheader" className="text-black">
            Loading plan details...
          </Text>
        </div>
      );
    }

    // Error loading products
    if (isProductsError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-8">
          <Text variant="smallSubheader" className="text-black">
            Failed to load plan details
          </Text>
          <Button
            variant="primary"
            onPress={() => history.push(paths.gettingStarted())}
          >
            Go Back
          </Button>
        </div>
      );
    }

    if (isCreateCheckoutLoading) {
      return (
        <CheckoutPageLayout onGoBack={handleBackFromCheckout}>
          <div className="flex flex-1 flex-col items-center justify-center gap-8">
            <Spinner size="large" />
            <Text variant="smallSubheader" className="text-black">
              Preparing checkout
            </Text>
          </div>
        </CheckoutPageLayout>
      );
    }

    if (isCheckoutProcessing) {
      return (
        <CheckoutPageLayout onGoBack={handleBackFromCheckout}>
          <div className="flex flex-1 flex-col items-center justify-center gap-8">
            <Spinner size="large" />
            <Text variant="smallSubheader" className="text-black">
              {isInitialCheckoutPolling
                ? 'Your order is almost ready'
                : 'Looks like this is taking longer than expected'}
            </Text>
          </div>
        </CheckoutPageLayout>
      );
    }

    if (isCheckoutCompleted) {
      // segment tracking
      analytics.trackStripeCheckoutSuccessPageLoaded({});
      return <CheckoutSuccessPage onViewInvoice={openStripeCustomerPortal} />;
    }

    if (isCheckoutError) {
      // segment tracking
      analytics.trackStripeCheckoutErrorPageLoaded({});
      return (
        <CheckoutErrorPage
          onGoBack={handleBackFromCheckout}
          onGoBackToPlanOptions={handleRetryPayment}
        />
      );
    }

    if (isCheckoutTimeout) {
      return (
        <CheckoutMaxRetryErrorPage
          onGoBack={handleBackFromCheckout}
          onGoToPaymentSubscriptions={openStripeCustomerPortal}
        />
      );
    }

    // Stripe embedded checkout Page
    if (showStripeEmbeddedCheckout) {
      // segment tracking
      analytics.trackStripeCheckoutLoaded({
        plan: 'pro',
        quantity: count,
        billingCycle,
      });
      return (
        <CheckoutPageLayout
          onGoBack={handleBackFromCheckout}
          className="max-w-[1056px]"
        >
          <div className="w-full mx-auto mt-4">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </CheckoutPageLayout>
      );
    }

    // Default: Plan selection Page
    return (
      <div className="flex flex-col md:flex-row gap-8 md:gap-12">
        <Card className="px-8 pt-8 pb-6 w-full max-w-[628px] flex flex-col gap-4">
          <Text className="text-[#202020]" variant="subheader">
            Plan Options
          </Text>

          {/* Billing Cycle Section is disabled for now */}
          {/* <BillingCycleSection
            plan={planDetail}
            billingCycle={billingCycle}
            onBillingCycleChange={handleBillingCycleChange}
          /> */}

          <NumberOfPlans
            count={count}
            onChange={handleInputChange}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onBlur={handleInputBlur}
          />

          <Divider className="border-[#D9D9D9]" />

          <div>
            {selectedPlan && <OrderDetails plan={selectedPlan} count={count} />}

            <Text className="text-gray-600 px-6 pt-1" variant="body">
              Shopping from outside the United States?
              <button
                onClick={openChat}
                className="text-blue-600 cursor-pointer hover:underline bg-transparent border-none p-0 font-inherit ml-1"
              >
                Chat with us
              </button>
            </Text>
          </div>

          <Button
            variant="primary"
            onPress={handleReviewOrder}
            isDisabled={isBlackFridayActive() && count > maxPlanCount}
          >
            Review Order
          </Button>
        </Card>
        <PlanDetailsCard plan={planDetail} />
      </div>
    );
  }, [
    isLoadingProducts,
    isProductsError,
    isCreateCheckoutLoading,
    isCheckoutProcessing,
    isCheckoutCompleted,
    isCheckoutError,
    isCheckoutTimeout,
    showStripeEmbeddedCheckout,
    isInitialCheckoutPolling,
    clientSecret,
    billingCycle,
    count,
    selectedPlan,
    maxPlanCount,
    planDetail,
    handleBackFromCheckout,
    handleRetryPayment,
    handleInputChange,
    handleIncrement,
    handleDecrement,
    handleInputBlur,
    handleReviewOrder,
    openStripeCustomerPortal,
    openChat,
    history,
  ]);

  return (
    <Page title="Plans - Displai">
      <PageLayout title={pageTitle}>{renderContent}</PageLayout>
    </Page>
  );
};

export default PlansPage;
